import * as React from 'react';

import { AnyCalendarDate } from '@internationalized/date';
import { BillRecord } from '@rematter/modules/finance/types';
import {
  Badge,
  DataGridColumn,
  LocalizedDate,
  Money,
  Row,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { BillStatusBadge } from '../../_shared/BillStatusBadge';
import { BillActionMenu } from '../BillActionMenu';
import { BillGroupCell } from '../BillGroupCell';
import { BillAccountColumnFilter } from '../filters/BillAccountColumnFilter';
import { BillFacilityColumnFilter } from '../filters/BillFacilityColumnFilter';
import { BillStatusColumnFilter } from '../filters/BillStatusColumnFilter';
import { BillTypeColumnFilter } from '../filters/BillTypeColumnFilter';

import {
  $columnSelectedAccountIds,
  $columnSelectedFacilityIds,
  $columnSelectedStatuses,
  $columnSelectedTypes,
} from '~/modules/finance/_state/financeBills.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

type Args = {
  setBillsToPay: (bills: BillRecord[] | null) => void;
  setIsPaymentDrawerOpen: (open: boolean) => void;
  onVoid: (bill: BillRecord) => void;
  onDoubleClick?: (record: BillRecord) => void;
  grouped: boolean;
};

export const useBillsListColumns = ({
  setBillsToPay,
  setIsPaymentDrawerOpen,
  onVoid,
  onDoubleClick,
  grouped,
}: Args) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const columnSelectedStatuses = useAtomValue($columnSelectedStatuses);
  const columnSelectedTypes = useAtomValue($columnSelectedTypes);
  const columnSelectedAccountIds = useAtomValue($columnSelectedAccountIds);
  const columnSelectedFacilityIds = useAtomValue($columnSelectedFacilityIds);

  // Memoized values
  const columns: DataGridColumn<BillRecord>[] = React.useMemo(() => {
    const handlePay = (bills: BillRecord | BillRecord[]) => {
      const billsArray = Array.isArray(bills) ? bills : [bills];
      setBillsToPay(billsArray);
      setIsPaymentDrawerOpen(true);
    };

    const allColumns: DataGridColumn<BillRecord>[] = [
      {
        key: 'documentNumber',
        header: t.columns.billNumber,
        cell: ({ row: record }) => record.documentNumber,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
        grow: grouped ? 1 : undefined,
        groupCell: (group) => <BillGroupCell {...group} />,
      },
      {
        key: 'accountName',
        header: t.columns.supplier,
        resizable: true,
        cell: ({ row: record }) => record.accountName,
        columnFilter: ({ close }) => (
          <BillAccountColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedAccountIds.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'type',
        header: t.columns.type,
        cell: ({ row: record }) => (
          <Badge>{t.enums.billType[record.type] ?? record.type}</Badge>
        ),
        columnFilter: ({ close }) => <BillTypeColumnFilter onClose={close} />,
        isFilterActive: columnSelectedTypes.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'facility',
        header: t.bills.list.columns.facility,
        width: 150,
        cell: ({ row: record }) => (
          <span className="truncate" title={record.facilityName}>
            {record.facilityName ?? '-'}
          </span>
        ),
        columnFilter: ({ close }) => (
          <BillFacilityColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedFacilityIds.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'documentDate',
        header: t.columns.transactionDate,
        sortable: true,
        cell: ({ row: record }) =>
          record.documentDate ? (
            <LocalizedDate
              date={record.documentDate as AnyCalendarDate}
              dateStyle="medium"
            />
          ) : (
            '-'
          ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'dueDate',
        header: t.columns.dueDate,
        cell: ({ row: record }) =>
          record.dueDate ? (
            <LocalizedDate
              date={record.dueDate as AnyCalendarDate}
              dateStyle="medium"
            />
          ) : (
            '-'
          ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'status',
        header: t.columns.status,
        sortable: true,
        cell: ({ row: record }) => <BillStatusBadge status={record.status} />,
        columnFilter: ({ close }) => <BillStatusColumnFilter onClose={close} />,
        isFilterActive: columnSelectedStatuses.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'balance',
        header: t.columns.balance,
        cell: ({ row: record }) =>
          record.openAmount ? (
            <Row xEnd>
              <Money {...record.openAmount} />
            </Row>
          ) : (
            '-'
          ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'currency',
        header: t.columns.currency,
        width: 70,
        cell: ({ row: record }) => record.amount.currencyCode,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row: record }) => (
          <BillActionMenu
            bill={record}
            onPay={handlePay}
            onVoid={onVoid}
            onViewDetails={(bill) => onDoubleClick?.(bill)}
          />
        ),
      },
    ];

    if (grouped) {
      return allColumns.filter(
        (col) => col.key !== 'accountName' && col.key !== 'accountType',
      );
    }

    return allColumns;
  }, [
    grouped,
    onDoubleClick,
    onVoid,
    setBillsToPay,
    setIsPaymentDrawerOpen,
    columnSelectedAccountIds,
    columnSelectedFacilityIds,
    columnSelectedStatuses,
    columnSelectedTypes,
    t,
  ]);

  return { columns };
};
